import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneInput } from "@/components/ui/phone-input";
import SignaturePad from "@/components/ui/signature-pad";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, ArrowLeft, ShieldCheck, Leaf } from "lucide-react";
import logo from "@/assets/logo-mente-sa.png";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import { translateFormConfig, normalizeLocale } from "@/lib/client-record-i18n";
import {
  ClientRecordFormConfig,
  FormField,
  loadFormConfig,
  CONSENT_TEXT,
  HEALTH_CONDITIONS,
  TREATMENT_TYPES,
  PRESSURE_LEVELS,
  AROMA_OPTIONS,
} from "@/lib/client-record-form";

export { HEALTH_CONDITIONS, TREATMENT_TYPES, PRESSURE_LEVELS, AROMA_OPTIONS, CONSENT_TEXT };

const TEXT_LIMITS: Record<string, number> = {
  full_name: 150,
  email: 255,
  phone: 40,
  address: 300,
  profession: 120,
  emergency_name: 150,
  emergency_phone: 40,
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-none border-none bg-transparent p-0 space-y-4">
    <h2 className="font-display text-lg font-bold text-foreground border-b border-muted pb-1 uppercase tracking-tight">{title}</h2>
    {children}
  </div>
);

const ALWAYS_REQUIRED = ["full_name", "email", "phone", "consent_accepted", "signature_data", "password"];

const ClientRecord = () => {
  const [config, setConfig] = useState<ClientRecordFormConfig | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [accountCreated, setAccountCreated] = useState(false);
  const [alreadyHasRecord, setAlreadyHasRecord] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [showAccountCreation, setShowAccountCreation] = useState(true);
  const [params] = useSearchParams();
  const { i18n } = useTranslation();
  const locale = normalizeLocale(i18n.language);
  const [baseConfig, setBaseConfig] = useState<ClientRecordFormConfig | null>(null);
  const [optionReverse, setOptionReverse] = useState<Record<string, string>>({});

  const get = (k: string) => values[k];
  const set = (k: string, v: any) => setValues((s) => ({ ...s, [k]: v }));

  // Idioma vindo do link enviado ao cliente (?lang=es)
  useEffect(() => {
    const langParam = params.get("lang");
    if (langParam) {
      const target = normalizeLocale(langParam);
      if (target !== i18n.language) i18n.changeLanguage(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFormConfig().then((c) => {
      setBaseConfig(c);
      setConfig(c);
      const init: Record<string, any> = {};
      c.sections.forEach((s) =>
        s.fields.forEach((f) => {
          if (f.type === "checkbox_group") init[f.key] = [];
          else if (f.type === "switch" || f.type === "yes_no") init[f.key] = "";
          else if (f.type === "chips" || f.type === "select") init[f.key] = f.options?.[0] || "";
          else if (f.type === "consent") init[f.key] = false;
          else if (f.type === "signature") init[f.key] = null;
          else init[f.key] = "";
        }),
      );
      init.pressure_preference = "Média";
      init.aroma_preference = "Sem preferência";
      init.staff_name = ""; // No longer pre-filling so client must select or admin assigns later
      setValues((prev) => ({ ...init, ...prev }));
    });
  }, []);

  // Traduz automaticamente a ficha para o idioma escolhido pelo cliente
  useEffect(() => {
    if (!baseConfig) return;
    let cancelled = false;
    translateFormConfig(baseConfig, locale).then(({ config: translated, reverse }) => {
      if (cancelled) return;
      setConfig(translated);
      setOptionReverse(reverse);
    });
    return () => { cancelled = true; };
  }, [baseConfig, locale]);

  // Pré-preenchimento com dados já existentes no sistema (email + telemóvel)
  useEffect(() => {
    const emailParam = (params.get("email") || "").trim();
    const phoneParam = (params.get("telefone") || "").trim();
    const nameParam = (params.get("nome") || "").trim();
    
    // Independent of URL params, we want to check if the user is typing an existing email/phone
    const checkExisting = async (e: string, p: string) => {
      if (!e && !p) return;
      // Using direct query since RPC type is not updated yet
      const { data, error } = await supabase
        .from("client_records" as any)
        .select("id")
        .or(`email.eq.${e},phone.eq.${p}`)
        .limit(1)
        .maybeSingle();
      
      if (!error) setShowAccountCreation(!data);
    };

    if (values.email || values.phone) {
      const timer = setTimeout(() => {
        checkExisting(values.email, values.phone);
      }, 500);
      return () => clearTimeout(timer);
    }

    if (!emailParam && !phoneParam && !nameParam) return;
    setValues((v) => ({
      ...v,
      email: emailParam || v.email,
      phone: phoneParam || v.phone,
      full_name: nameParam || v.full_name,
    }));
    
    if (!emailParam || !phoneParam) return;
    (async () => {
      const { data, error } = await supabase.rpc("get_client_prefill", { p_email: emailParam, p_phone: phoneParam });
      if (error || !data) return;
      const d = data as any;
      if (!d.found) return;
      setValues((v) => ({
        ...v,
        full_name: v.full_name || d.full_name || "",
        phone: v.phone || d.phone || "",
        address: v.address || d.address || "",
      }));
      setPrefilled(true);
      if (d.has_record) setAlreadyHasRecord(true);
      setShowAccountCreation(!d.has_record);
    })();
  }, [params, values.email, values.phone]);

  const activeFields = (): FormField[] =>
    (config?.sections || [])
      .filter((s) => s.enabled && (s.id !== "account" || showAccountCreation))
      .flatMap((s) => s.fields)
      .filter((f) => f.enabled);

  const isVisible = (f: FormField) => {
    if (!f.showIf) return true;
    const triggerValue = get(f.showIf);
    // Para campos yes_no ou switch, mostramos se for "Sim" ou true
    // Se o triggerValue for "Sim" ou true, o campo dependente deve ser visível
    return triggerValue === "Sim" || triggerValue === true;
  };

  const handleSubmit = async () => {
    if (!config) return;
    const fields = activeFields().filter(isVisible);

    for (const f of fields) {
      const required = f.required || ALWAYS_REQUIRED.includes(f.key);
      if (!required) continue;
      const v = get(f.key);
      if ((f.type === "consent" || f.type === "yes_no" || f.type === "signature") && !v) {
        const errorMsgs: Record<string, string> = {
          consent: "É necessário aceitar o consentimento.",
          signature: "A assinatura digital é obrigatória.",
          yes_no: `Selecione uma opção em "${f.label}".`,
        };
        toast.error(errorMsgs[f.type as string] || `Preencha o campo "${f.label}".`);
        return;
      }
      if (f.type === "password") continue;
      if (f.type === "checkbox_group" ? !(v || []).length : typeof v === "string" ? !v.trim() : v === null || v === undefined) {
        toast.error(`Preencha o campo "${f.label}".`);
        return;
      }
    }

    const emailField = fields.find((f) => f.key === "email");
    if (emailField && !/^\S+@\S+\.\S+$/.test(String(get("email") || "").trim())) {
      toast.error("Email inválido.");
      return;
    }

    const passwordActive = fields.some((f) => f.key === "password");
    if (passwordActive) {
      if (password.length < 6) {
        toast.error("Defina uma palavra-passe com pelo menos 6 caracteres.");
        return;
      }
      if (password !== password2) {
        toast.error("As palavras-passe não coincidem.");
        return;
      }
    }

    // Os dados são sempre guardados em PT-PT (base) para o painel admin
    const toBase = (v: any): any => {
      if (typeof v === "string") return optionReverse[v] || v;
      if (Array.isArray(v)) return v.map(toBase);
      return v;
    };

    const str = (k: string, max = 1000) => {
      const v = String(get(k) ?? "").trim().slice(0, max);
      return v || null;
    };

    const baseLabels: Record<string, string> = {};
    baseConfig?.sections.forEach((sec) =>
      sec.fields.forEach((f) => { baseLabels[f.key] = f.label; }),
    );

    const custom: Record<string, any> = {};
    fields.filter((f) => !f.system || f.key === "password").forEach((f) => {
      const v = f.key === "password" ? password : get(f.key);
      if (v === "" || v === null || v === undefined) return;
      custom[baseLabels[f.key] || f.label || f.key] = toBase(v);
    });

    const health = get("health_conditions") || [];
    if (get("is_pregnant") === "Sim") health.push("Gestante");
    if (get("has_hypertension") === "Sim") health.push("Hipertensão");
    if (get("has_heart_problem") === "Sim") health.push("Problema cardíaco");
    if (get("has_thrombosis") === "Sim") health.push("Trombose");
    if (get("has_surgery") === "Sim") health.push("Cirurgia recente");
    if (get("has_allergies") === "Sim") health.push("Alergias");

    const treatments = get("treatment_types") || [];
    if (get("treat_relax") === "Sim") treatments.push("Massagem relaxante");
    if (get("treat_drain") === "Sim") treatments.push("Drenagem linfática");
    if (get("treat_vaccum") === "Sim") treatments.push("Vacuoterapia glúteos");

    const pregWeeks = get("pregnancy_weeks") ? parseInt(String(get("pregnancy_weeks")), 10) : null;

    setSaving(true);
    
    // Check if a record already exists with this email to prevent duplication (primary key for linking)
    const { data: existingRecord } = await supabase
      .from("client_records")
      .select("id")
      .ilike("email", String(get("email") || "").trim())
      .maybeSingle();

    const recordData = {
      full_name: String(get("full_name") || "").trim().slice(0, 150),
      email: String(get("email") || "").trim().toLowerCase().slice(0, 255),
      phone: String(get("phone") || "").trim().slice(0, 40),
      assigned_staff: get("staff_name") && get("staff_name") !== "Outro" ? [String(get("staff_name")).trim()] : [],
      birth_date: get("birth_date") || null,
      address: str("address", 300),
      profession: str("profession", 120),
      emergency_name: str("emergency_name", 150),
      emergency_phone: str("emergency_phone", 40),
      health_conditions: toBase(health),
      allergies: str("allergies"),
      oil_allergies: str("oil_allergies"),
      avoid_areas: str("avoid_areas"),
      treatment_types: toBase(treatments),
      pressure_preference: toBase(get("pressure_preference")) || null,
      likes_essential_oils: get("likes_essential_oils") === "Sim" || !!get("likes_essential_oils"),
      aroma_consent: get("aroma_consent") === "Sim",
      aroma_preference: toBase(get("aroma_preference")) || null,
      medications: str("medications"),
      surgeries: str("surgeries"),
      is_pregnant: get("is_pregnant") === "Sim",
      pregnancy_weeks: pregWeeks,
      goals: str("goals"),
      observations: str("observations"),
      custom_fields: custom,
      consent_accepted: true,
      consent_text: config.consent_text,
      signature_data: get("signature_data") || null,
      signed_at: new Date().toISOString(),
      locale,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (existingRecord?.id) {
      const { error: updateError } = await supabase
        .from("client_records")
        .update(recordData)
        .eq("id", existingRecord.id);
      error = updateError;
    } else {
      const { error: insertError } = await supabase
        .from("client_records")
        .insert([recordData]);
      error = insertError;
    }

    if (!error) {
      // Notificar o colaborador por WhatsApp sobre o preenchimento da ficha
      try {
        const staffNameFromValues = String(get("staff_name") || "").trim();
        
        await supabase.functions.invoke("notify-staff-client-record", {
          body: {
            client_name: String(get("full_name") || "").trim(),
            client_email: String(get("email") || "").trim().toLowerCase(),
            client_phone: String(get("phone") || "").trim(),
            staff_name_selection: staffNameFromValues === "Outro" ? null : staffNameFromValues,
            record_data: {
              health_conditions: health,
              treatment_types: treatments,
              pressure_preference: get("pressure_preference"),
              aroma_consent: get("aroma_consent") === "Sim",
            },
          },
        });
      } catch (e) {
        console.error("Failed to notify staff:", e);
      }
    }

    if (error) {
      setSaving(false);
      toast.error("Não foi possível enviar a ficha. Tente novamente.");
      return;
    }

    if (passwordActive) {
      const email = String(get("email") || "").trim().toLowerCase();
      const cleanPassword = password.trim();
      
      // We still attempt frontend signUp for session hydration if possible,
      // but the database trigger check_and_create_client_auth (if active)
      // will handle the user creation if the frontend fails or is skipped.
      try {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password: cleanPassword,
          options: {
            emailRedirectTo: `${window.location.origin}/minha-conta`,
            data: { 
              full_name: String(get("full_name") || "").trim(),
              phone: String(get("phone") || "").trim()
            },
          },
        });
        
        if (!signUpError) {
          setAccountCreated(true);
          // Force link via RPC
          await supabase.rpc("link_client_account");
          
          if (signUpData.session) {
            toast.success("Conta criada e sessão iniciada!");
          } else {
            toast.success("Registo concluído! Pode agora aceder à sua área de cliente.");
          }
        }
      } catch (e) {
        console.error("Auth process error:", e);
      }
    }

    setSaving(false);
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderField = (f: FormField) => {
    if (!isVisible(f)) return null;
    const required = f.required || ALWAYS_REQUIRED.includes(f.key);
    const star = required ? " *" : "";
    const value = get(f.key);

    switch (f.type) {
      case "textarea":
        return (
          <div key={f.key} className="space-y-1">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <Textarea
              rows={1}
              placeholder={f.placeholder}
              value={value || ""}
              onChange={(e) => set(f.key, e.target.value)}
              maxLength={1000}
              className="border-0 border-b border-dashed border-muted-foreground/30 rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0 py-0.5 h-auto min-h-[32px] resize-none text-sm"
            />
            {f.hint && <p className="text-[10px] text-muted-foreground italic leading-none">{f.hint}</p>}
          </div>
        );
      case "phone":
        return (
          <div key={f.key} className="space-y-1">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <PhoneInput value={value || ""} onChange={(v) => set(f.key, v)} placeholder={f.placeholder || "9XX XXX XXX"} className="h-8" />
          </div>
        );
      case "email":
        return (
          <div key={f.key} className="space-y-1">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <Input
              type="email"
              placeholder={f.placeholder}
              value={value || ""}
              onChange={(e) => set(f.key, e.target.value)}
              maxLength={255}
              className="border-0 border-b border-dashed border-muted-foreground/30 rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0 py-0.5 h-8 text-sm lowercase"
            />
            {f.hint && <p className="text-[10px] text-muted-foreground italic leading-none">{f.hint}</p>}
          </div>
        );
      case "date":
      case "number":
      case "text":
        return (
          <div key={f.key} className="space-y-1">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <Input
              type={f.type === "text" ? "text" : f.type}
              placeholder={f.placeholder}
              value={value || ""}
              onChange={(e) => set(f.key, e.target.value)}
              maxLength={TEXT_LIMITS[f.key] || 300}
              className="border-0 border-b border-dashed border-muted-foreground/30 rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0 py-0.5 h-8 text-sm"
            />
            {f.hint && <p className="text-[10px] text-muted-foreground italic leading-none">{f.hint}</p>}
          </div>
        );
      case "switch":
        // Override switch to use yes_no style per user request: "tudo de marcar a respostas que tiver sim ou nao , e nao de assinalar como ativo"
        const switchOpts = ["Sim", "Não"];
        return (
          <div key={f.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1 border-b border-dotted border-muted/50">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <div className="flex gap-2">
              {switchOpts.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => set(f.key, o === "Sim")}
                  className="flex items-center gap-1.5 group transition-all"
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                    (o === "Sim" && value === true) || (o === "Não" && value === false)
                      ? "border-primary bg-primary/10" 
                      : "border-muted-foreground/40 group-hover:border-primary"
                  }`}>
                    {((o === "Sim" && value === true) || (o === "Não" && value === false)) && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className={`text-[13px] ${(o === "Sim" && value === true) || (o === "Não" && value === false) ? "text-primary font-bold" : "text-foreground/70"}`}>{o}</span>
                </button>
              ))}
            </div>
          </div>
        );
      case "checkbox_group":
        const groupOpts = f.options || [];
        return (
          <div key={f.key} className="space-y-2">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
              {groupOpts.map((o) => {
                const current = Array.isArray(value) ? value : [];
                const isSelected = current.includes(o);
                return (
                  <button
                    key={o}
                    type="button"
                    onClick={() => {
                      const next = isSelected 
                        ? current.filter(item => item !== o)
                        : [...current, o];
                      set(f.key, next);
                    }}
                    className="flex items-center gap-1.5 group text-left py-0.5"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                      isSelected 
                        ? "border-primary bg-primary/10" 
                        : "border-muted-foreground/40 group-hover:border-primary"
                    }`}>
                      {isSelected && <div className="w-2 h-2 bg-primary" />}
                    </div>
                    <span className={`text-[13px] ${isSelected ? "text-primary font-bold" : "text-foreground/70"}`}>{o}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      case "yes_no":
        const opts = f.options && f.options.length > 0 ? f.options : ["Sim", "Não"];
        return (
          <div key={f.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-1 border-b border-dotted border-muted/50">
            <Label className="text-[14px] font-bold text-foreground/90">{f.label}{star}</Label>
            <div className="flex gap-2">
              {opts.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => set(f.key, o)}
                  className="flex items-center gap-1.5 group transition-all"
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                    value === o 
                      ? "border-primary bg-primary/10" 
                      : "border-muted-foreground/40 group-hover:border-primary"
                  }`}>
                    {value === o && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className={`text-[13px] ${value === o ? "text-primary font-bold" : "text-foreground/70"}`}>{o}</span>
                </button>
              ))}
            </div>
          </div>
        );
      case "chips":
        return (
          <div key={f.key} className="space-y-2">
            <Label>{f.label}{star}</Label>
            <div className="flex flex-wrap gap-2">
              {(f.options || []).map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => set(f.key, o)}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    value === o ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted/50"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        );
      case "select":
        return (
          <div key={f.key} className="space-y-2">
            <Label>{f.label}{star}</Label>
            <Select value={value || ""} onValueChange={(v) => set(f.key, v)}>
              <SelectTrigger><SelectValue placeholder={f.placeholder || "Selecione..."} /></SelectTrigger>
              <SelectContent>
                {(f.options || []).map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "consent":
        return (
          <div key={f.key} className="space-y-3">
            <div className="rounded-xl bg-muted/50 p-4 text-xs leading-relaxed text-muted-foreground max-h-56 overflow-y-auto">
              {config?.consent_text}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={!!value} onCheckedChange={(v) => set(f.key, !!v)} className="mt-0.5" />
              <span className="text-sm">{f.label}{star}</span>
            </label>
          </div>
        );
      case "signature":
        return (
          <div key={f.key} className="space-y-6">
            <SignaturePad value={value} onChange={(v) => set(f.key, v)} label={f.label} />
            <div className="flex flex-col sm:flex-row items-center justify-between gap-8 pt-4 border-t border-dashed border-border mt-8">
              <div className="text-center sm:text-left flex-1">
                <div className="h-[2px] w-full max-w-[200px] bg-foreground/20 mb-2 mx-auto sm:mx-0" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Assinatura do cliente</p>
              </div>
              <div className="text-center sm:text-right flex-1">
                <div className="h-[2px] w-full max-w-[200px] bg-foreground/20 mb-2 mx-auto sm:ml-auto sm:mr-0" />
                <p className="text-sm font-display font-bold text-foreground">Thaiane de Freitas</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">Mente Sã</p>
              </div>
            </div>
            <p className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground mt-4">
              <ShieldCheck size={12} /> Os seus dados são armazenados de forma segura e apenas acessíveis à equipa Mente Sã.
            </p>
          </div>
        );
      case "password":
        return (
          <div key={f.key} className="space-y-4">
            {f.hint && <p className="text-sm text-muted-foreground">{f.hint}</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{f.label} *</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} />
              </div>
              <div className="space-y-2">
                <Label>Confirmar {f.label.toLowerCase()} *</Label>
                <Input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} minLength={6} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              O acesso é feito com o email indicado acima ({get("email") || "o seu email"}).
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (alreadyHasRecord) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8 space-y-4">
          <ShieldCheck size={48} className="mx-auto text-primary" />
          <h1 className="font-display text-2xl font-bold">Ficha já preenchida</h1>
          <p className="text-sm text-muted-foreground">
            Já temos a sua Ficha de Avaliação e Consentimento registada em segurança. Se algo mudou no seu estado de
            saúde ou se deseja preencher a ficha novamente, solicite por email ou WhatsApp e a nossa equipa reabre a
            ficha para si.
          </p>
          <Button asChild className="rounded-full w-full">
            <Link to="/minha-conta">Aceder à Área do Cliente</Link>
          </Button>
          <Button asChild variant="ghost" className="rounded-full w-full">
            <Link to="/">Voltar ao início</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8 space-y-4">
          <CheckCircle2 size={48} className="mx-auto text-primary" />
          <h1 className="font-display text-2xl font-bold">Ficha enviada com sucesso!</h1>
          <p className="text-sm text-muted-foreground">
            Obrigada, {String(get("full_name") || "").split(" ")[0]}. A sua ficha de avaliação e consentimento ficou
            registada em segurança. A nossa equipa terá acesso a ela em cada sessão.
          </p>
          {accountCreated && (
            <p className="text-sm text-muted-foreground">
              Criámos a sua conta com o email <strong>{String(get("email") || "").trim().toLowerCase()}</strong>. Confirme
              o email (se lhe for pedido) e entre na Área do Cliente para ver as suas reservas e pedir alterações.
            </p>
          )}
          <Button asChild className="rounded-full w-full">
            <Link to="/minha-conta">Ir para a Área do Cliente</Link>
          </Button>
          <Button asChild variant="ghost" className="rounded-full w-full">
            <Link to="/">Voltar ao início</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f1f3f5] py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-none p-4 sm:p-12 border border-border">
        <header className="mb-8 border-b border-primary/30 pb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Mente Sã" className="h-12 w-12 rounded-full object-cover border border-primary/20 p-0.5" />
            <div className="text-center sm:text-left">
              <h1 className="font-display font-bold text-xl text-primary leading-tight">Mente Sã</h1>
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest italic">Aromaterapia & Bem-estar</p>
            </div>
          </div>
          <div className="text-center sm:text-right">
            <h2 className="font-display font-bold text-lg text-foreground/90 leading-tight">Ficha de Avaliação e Consentimento</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">Registo Confidencial</p>
            <div className="mt-2 flex justify-center sm:justify-end scale-90 origin-right">
              <LanguageSelector />
            </div>
          </div>
        </header>

        {prefilled && (
          <div className="mb-8 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 italic">
            Pré-preenchemos alguns campos com os dados da sua reserva. Confirme se está tudo correto.
          </div>
        )}

        <main className="space-y-8">
          {!config && <p className="text-sm text-muted-foreground text-center py-12">A carregar formulário...</p>}

          {config?.sections
            .filter((s) => s.enabled && s.fields.some((f) => f.enabled) && (s.id !== "account" || showAccountCreation))
            .map((s) => (
              <Section key={s.id} title={s.title}>
                {s.id === "personal" ? (
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                    {s.fields.filter((f) => f.enabled).map((f) => (
                      <div key={f.key} className={["full_name", "address"].includes(f.key) ? "sm:col-span-2" : ""}>
                        {renderField(f)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {s.fields.filter((f) => f.enabled).map((f) => renderField(f))}
                  </div>
                )}
              </Section>
            ))}

          <div className="pt-6">
            <Button className="w-full rounded-none h-12 text-base font-bold shadow-md transition-all hover:brightness-95 bg-primary" disabled={saving || !config} onClick={handleSubmit}>
              {saving ? "A processar..." : "Finalizar e Enviar Ficha"}
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientRecord;
